import React, {FC} from 'react';
import styled from 'styled-components';

import {AvatarCell} from '../../../../../../../../../src/components/common/table/cells/avatarCell';
import {CardCell} from '../../../../../../../../../src/components/common/table/cells/cardCell';
import {CardGroupCell} from '../../../../../../../../../src/components/common/table/cells/cardGroupCell';
import {EmptyCell} from '../../../../../../../../../src/components/common/table/cells/emptyCell';
import {HandleCellContainer} from '../../../../../../../../../src/components/common/table/cells/handleCellContainer';
import {TextCell} from '../../../../../../../../../src/components/common/table/cells/textCell';
import {convertAccountToAvatarCellModel} from '../../../../../../../../../src/components/common/table/helpers/tableCellHelpers';
import {
  handleVirtualTableCheckboxSelect,
  handleVirtualTableRowSelect,
} from '../../../../../../../../../src/components/common/table/helpers/tableHelpers';
import {TableColumn} from '../../../../../../../../../src/components/common/table/tableColumn';
import {
  TableRow,
  TableRowRenderOptions,
} from '../../../../../../../../../src/components/common/table/tableRow';
import {getCardHandlesByType} from '../../../../../../../../../src/helpers/models/cardHelpers';
import {AccountModel} from '../../../../../../../../../src/models/api/accountModel';
import {CardGroupModel} from '../../../../../../../../../src/models/api/cardGroupModel';
import {CardModel} from '../../../../../../../../../src/models/api/cardModel';
import {ContactSourcesEnum} from '../../../../../../../../../src/models/api/contactModel';
import {
  LocalPreferencesSizes,
  LocalPreferencesSizesEnum,
} from '../../../../../../../../../src/models/api/localPreferencesModel';
import {MessageTypesEnum} from '../../../../../../../../../src/models/api/messageModel';
import {
  Handler,
  HandlerOf,
  HandlerOfWithOptional,
} from '../../../../../../../../shared/helpers/src/react/reactHelpers';
import {VisualSizesEnum} from '../../../../../../../../shared/ui/src/styles/commonStyles';
import {renderCustomFieldCell} from '../contactsManagerCustomFields';
import {SharedColumnsEnum} from '../sharedTableConfig';
import {ContactsTableColumnsEnum, ContactsTableDefaultColumnsEnum} from './contactsTableConfig';

/*
 * Props.
 */

interface ContactsTableRowProps {
  isSelected: boolean;
  tableColumns: TableColumn<ContactsTableColumnsEnum>[];
  card?: CardModel;
  account?: AccountModel;
  options: TableRowRenderOptions;
  sizes: LocalPreferencesSizes;
  isPreviousSiblingSelected: boolean;
  isNextSiblingSelected: boolean;
  isInMultiSelectMode?: boolean;
  cardGroups?: ReadonlyArray<CardGroupModel>;
  onSelect: Handler;
  onMultiSelect: Handler;
  onCheckBoxToggle: HandlerOf<boolean>;
  onOpenAccountDetailsPopUp?: HandlerOfWithOptional<number, number>;
  openContactDetailsPopUp: HandlerOf<string>;
}

/*
 * Style.
 */

interface StyledTableRowProps {
  $minHeight: number;
}
const StyledTableRow = styled(TableRow)<StyledTableRowProps>`
  min-height: ${(p) => p.$minHeight}px;
`;

/*
 * Component.
 */

export const ContactsTableRow: FC<ContactsTableRowProps> = ({
  isSelected,
  card,
  account,
  options,
  tableColumns,
  sizes,
  isNextSiblingSelected,
  isPreviousSiblingSelected,
  isInMultiSelectMode,
  cardGroups,
  onSelect,
  onMultiSelect,
  onCheckBoxToggle,
  onOpenAccountDetailsPopUp,
  openContactDetailsPopUp,
}) => {
  // TODO: Remove the card id from the list of card ids in the table so empty row is not rendered
  if (!card) {
    return null;
  }

  const cellRenderer = (
    index: number,
    column: TableColumn<ContactsTableColumnsEnum>,
    isHovering: boolean,
    shouldShowStickyGradient: boolean,
  ) => {
    const emails = getCardHandlesByType(card, ContactSourcesEnum.EMAIL);
    const twitters = getCardHandlesByType(card, ContactSourcesEnum.TWITTER);
    const phoneNumbers = getCardHandlesByType(card, ContactSourcesEnum.PHONE);

    switch (column.type) {
      case ContactsTableDefaultColumnsEnum.NAME:
        return (
          <CardCell
            key={ContactsTableDefaultColumnsEnum.NAME}
            value={card}
            settings={column.settings}
            isHovering={isHovering}
            isSelected={isSelected}
            size={options.size}
            index={index}
            avatarSize={VisualSizesEnum.LARGE}
            shouldShowStickyGradient={shouldShowStickyGradient}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
          />
        );
      case ContactsTableDefaultColumnsEnum.ACCOUNT:
        return (
          <AvatarCell
            key={ContactsTableDefaultColumnsEnum.ACCOUNT}
            value={account && convertAccountToAvatarCellModel(account)}
            settings={column.settings}
            isHovering={isHovering}
            isSelected={isSelected}
            size={options.size}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
            onSelect={(event) => {
              event.preventDefault();
              if (account && onOpenAccountDetailsPopUp) {
                onOpenAccountDetailsPopUp(
                  account.id,
                  sizes[LocalPreferencesSizesEnum.CONTACTS_MANAGER_SIDEPANEL],
                );
              }
            }}
          />
        );
      case ContactsTableDefaultColumnsEnum.EMAIL:
        return (
          <HandleCellContainer
            key={ContactsTableDefaultColumnsEnum.EMAIL}
            value={emails}
            settings={column.settings}
            isHovering={isHovering}
            isSelected={isSelected}
            size={options.size}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
            source={ContactSourcesEnum.EMAIL}
            messageType={MessageTypesEnum.EMAIL}
          />
        );
      case ContactsTableDefaultColumnsEnum.PHONE:
        return (
          <HandleCellContainer
            key={ContactsTableDefaultColumnsEnum.PHONE}
            value={phoneNumbers}
            settings={column.settings}
            isHovering={isHovering}
            isSelected={isSelected}
            size={options.size}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
            source={ContactSourcesEnum.PHONE}
            messageType={MessageTypesEnum.SMS}
          />
        );
      case ContactsTableDefaultColumnsEnum.TWITTER:
        return (
          <HandleCellContainer
            key={ContactsTableDefaultColumnsEnum.TWITTER}
            value={twitters}
            settings={column.settings}
            isHovering={isHovering}
            isSelected={isSelected}
            size={options.size}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
            source={ContactSourcesEnum.TWITTER}
            messageType={MessageTypesEnum.TWEET}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
          />
        );
      case ContactsTableDefaultColumnsEnum.DESCRIPTION:
        return (
          <TextCell
            key={ContactsTableDefaultColumnsEnum.DESCRIPTION}
            value={card.description}
            settings={column.settings}
            isHovering={isHovering}
            isSelected={isSelected}
            size={options.size}
            index={index}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case SharedColumnsEnum.DYNAMIC: {
        // The column id is mapped to be the custom field id
        const customFieldValue = card.customFieldAttributes?.find((cf) => cf.id === Number(column.id))?.value;
        return renderCustomFieldCell({
          customFieldValue,
          index,
          column,
          isHovering,
          isSelected,
          shouldShowStickyGradient,
          options,
          isPreviousSiblingSelected,
          isNextSiblingSelected,
          onTeammateSelect: (contactUid) => openContactDetailsPopUp(contactUid),
        });
      }
      case ContactsTableDefaultColumnsEnum.LISTS:
        return (
          <CardGroupCell
            key={ContactsTableDefaultColumnsEnum.LISTS}
            value={cardGroups}
            settings={column.settings}
            isHovering={isHovering}
            isSelected={isSelected}
            size={options.size}
            index={index}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      default: {
        return (
          <EmptyCell
            key={column.type}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            isSelected={isSelected}
            index={index}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
          />
        );
      }
    }
  };

  return (
    <StyledTableRow
      onSelect={(event) =>
        handleVirtualTableRowSelect(event, {
          isSelected,
          isInMultiSelectMode,
          onSelect,
          onCheckBoxToggle,
          onMultiSelect,
        })
      }
      onCheckBoxClick={(event) =>
        handleVirtualTableCheckboxSelect(event, {isSelected, onCheckBoxToggle, onMultiSelect})
      }
      renderColumn={(isHovering) =>
        tableColumns.map((column, index) =>
          cellRenderer(index, column, isHovering, Boolean(options.shouldShowStickyGradient)),
        )
      }
      hasHover
      isSelected={isSelected}
      $minHeight={options.tableRowHeight}
      shouldShowRightGradient={options.shouldShowRightGradient}
      shouldAllowMultiSelect={options.shouldAllowMultiSelect}
      isCheckboxesVisible={isInMultiSelectMode}
    />
  );
};
